import { Check, Download, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { formatExportTimestamp } from '../utils/formatters'
import type { ExportStatus } from '../types'

type ListExportStatusCardProps = {
  status: ExportStatus
  isBusy?: boolean
  onDismiss?: () => void
  className?: string
  testId?: string
}

/**
 * Reimbursements list export status popup — white card with left accent,
 * status icon, “Export completed” / Created At, and blue Download button.
 */
export function ListExportStatusCard({
  status,
  isBusy = false,
  onDismiss,
  className,
  testId = 'reimbursements-list-export-status',
}: ListExportStatusCardProps) {
  const state = String(status.status || '').toUpperCase()
  const completed = state === 'COMPLETED' && Boolean(status.file_path)
  const failed = state === 'FAILED'
  const processing = isBusy || state === 'PROCESSING' || (!completed && !failed)

  const createdLabel = status.created_at
    ? `Created At: ${formatExportTimestamp(status.created_at)}`
    : null

  const title = completed
    ? 'Export completed'
    : failed
      ? 'Export failed'
      : 'Export in progress'

  return (
    <div
      data-testid={testId}
      className={cn(
        'flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white py-3 pl-3.5 pr-3 shadow-lg',
        className,
      )}
    >
      {processing ? (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-50 text-[rgb(21_151_255)]">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
        </span>
      ) : failed ? (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
          <X className="h-5 w-5" aria-hidden />
        </span>
      ) : (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-50 text-[rgb(21_151_255)]">
          <Check className="h-5 w-5" strokeWidth={2.5} aria-hidden />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold leading-tight text-slate-900">{title}</p>
        {createdLabel ? (
          <p className="mt-0.5 truncate text-xs font-normal text-slate-500">{createdLabel}</p>
        ) : null}
      </div>

      {completed && status.file_path ? (
        <a
          href={status.file_path}
          target="_blank"
          rel="noreferrer"
          data-testid={`${testId}-download`}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[rgb(21_151_255)] px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0d7acc]"
        >
          <Download className="h-4 w-4" aria-hidden />
          Download
        </a>
      ) : onDismiss && !processing ? (
        <button
          type="button"
          onClick={onDismiss}
          data-testid={`${testId}-dismiss`}
          className="shrink-0 text-xs font-semibold text-[rgb(21_151_255)] hover:underline"
        >
          Dismiss
        </button>
      ) : null}
    </div>
  )
}
